import CircularProgress from "@mui/material/CircularProgress";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { RobotoButton } from "@/shared/components";
import { OrgRecord, OrganizationTier } from "@/shared/domain/orgs";
import { StarIcon } from "@/shared/foundation/icons/mui";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

interface OrgUpdateTierButtonProps {
  orgId: string;
  currentOrgTier: OrganizationTier;
  onSuccess?: (updatedOrg: OrgRecord) => void;
  onError?: (error: unknown) => void;
}

export const OrgUpdateTierButton: React.FC<OrgUpdateTierButtonProps> = ({
  orgId,
  currentOrgTier,
  onSuccess,
  onError,
}) => {
  const { orgService } = useDomainServices();

  const [isUpdating, setIsUpdating] = React.useState(false);

  const differentTier: OrganizationTier =
    currentOrgTier === OrganizationTier.Free
      ? OrganizationTier.Premium
      : OrganizationTier.Free;

  const buttonText =
    currentOrgTier === OrganizationTier.Free
      ? "Upgrade to Premium"
      : "Downgrade to Free";

  async function updateOrgTier() {
    setIsUpdating(true);
    try {
      const updatedOrg = await orgService.updateOrgTier(orgId, differentTier);
      onSuccess?.(updatedOrg);
    } catch (e: unknown) {
      ErrorMonitoringService.captureError(e);
      onError?.(e);
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <RobotoButton
      eventName={"UpdateOrgTierClicked"}
      eventProperties={{
        orgId,
        tier: differentTier,
      }}
      onClick={() => {
        void updateOrgTier();
      }}
      disabled={isUpdating}
      startIcon={
        isUpdating ? (
          <CircularProgress size={14} thickness={5} color="inherit" />
        ) : (
          <StarIcon fontSize="small" />
        )
      }
      variant="outlined"
    >
      {isUpdating ? "Updating…" : buttonText}
    </RobotoButton>
  );
};
